import GradingMethod from '@linc-technologies/linced-common/enums/grading-method';
import type Grade from '@linc-technologies/linced-common/models/assessments/grade';
import type GradescheduleSelectvalue from '@linc-technologies/linced-common/models/assessments/gradeschedule-selectvalue';
import { findBy } from '@linc-technologies/linced-common/utils/arrays';

// Some types below stand in for models and fragments. The utils read deep
// get() paths, and the model types do not type these paths.
// Each type lists only the fields that these utils read.

// A select option.
export type GradeOption = GradescheduleSelectvalue;

// TS-STANDIN(assessments/gradeschedule-select): the `values` fragment array.
// The utils read it as an array.
export type GradeOptions = readonly GradeOption[];

// TS-STANDIN(assessments/gradeschedule-numeric): the range and stepping.
export interface NumericGradeSchedule {
  get(key: 'defaultNumericStepping' | 'range.start' | 'range.end'): number;
}

// TS-STANDIN(assessments/module): the schedule and stepping.
export interface GradeModule {
  numericStepping?: number | null;
  get(key: 'gradeSchedule.awardMethod'): number | null | undefined;
  get(key: 'gradeSchedule.numeric'): NumericGradeSchedule;
  get(key: 'gradeSchedule.select.values'): GradeOptions | null | undefined;
}

export interface NumericGrade {
  value: number;
  label: number;
}

// `awarded` is a number for numeric grades and a string for other grades.
export type AssessmentGrade = Grade;

export interface GradeCount {
  grade: unknown;
  learners: unknown[];
}

// TS-STANDIN(assessments/grade): the serialized hash.
export interface ScalableGrade {
  numericAwarded?: number | null;
  numericScale?: number | null;
}

function getPossibleNumericGrades(module: GradeModule): NumericGrade[] {
  const schedule = module.get('gradeSchedule.numeric');
  const stepping =
    module.numericStepping || schedule.get('defaultNumericStepping');
  const start = schedule.get('range.start');
  const end = schedule.get('range.end');
  const grades: NumericGrade[] = [];

  for (let i = start; i <= end; i += stepping) {
    grades.push({
      value: i,
      label: i,
    });
  }

  return grades;
}

export function getPossibleGrades(
  module: GradeModule,
): NumericGrade[] | GradeOptions | null | undefined {
  switch (module.get('gradeSchedule.awardMethod')) {
    case GradingMethod.NUMERIC:
      return getPossibleNumericGrades(module);
    default:
      return module.get('gradeSchedule.select.values');
  }
}

// possibleGrades is only read for numeric grades, to bucket them.
// No caller uses this copy. linced-app uses its own app/utils/grades.js.
export function gradesReducer(possibleGrades: readonly NumericGrade[]) {
  return (counts: GradeCount[], grade: AssessmentGrade): GradeCount[] => {
    let awarded: readonly unknown[] | Grade['multiSelectAwarded'] = [
      grade.awarded,
    ];
    let numericAwarded: NumericGrade | undefined;
    switch (grade.gradeType) {
      case GradingMethod.MULTIPLE_SELECT:
        awarded = grade.multiSelectAwarded;
        break;

      case GradingMethod.NUMERIC:
        // Numeric grades need to be bucketed.
        numericAwarded = possibleGrades.find((possible, idx) => {
          let nextPossible = possible;
          if (idx < possibleGrades.length - 1) {
            nextPossible = possibleGrades[idx + 1] as NumericGrade;
          }
          // Numeric grades award a number.
          const value = grade.awarded as number;
          switch (idx) {
            case 0:
              // Lowest possible grade.
              // Include everything below the next grade.
              return value < nextPossible.value;
            case possibleGrades.length - 1:
              // Highest possible grade.  Include it and above.
              return value >= possible.value;
            default:
              // All grades in between.  Include it, and everything above
              // it but below the next possible grade.
              return possible.value <= value && value < nextPossible.value;
          }
        });
        // find() gives a possible grade object or undefined.
        if (numericAwarded !== undefined) {
          awarded = [numericAwarded.value];
        }
        break;
    }

    awarded.forEach((value) => {
      let count = findBy(counts, 'grade', value);
      if (count === undefined) {
        count = {
          grade: value,
          learners: [grade.person],
        };
        counts.push(count);
      } else {
        count.learners.push(grade.person);
      }
    });
    return counts;
  };
}

// scaleNumericGrade calculates and applies the scale factor for a numeric Grade.
export function scaleNumericGrade<T extends ScalableGrade>(grade: T): T {
  // Calculate numeric scale if applicable.
  const awarded = grade.numericAwarded ?? 0;
  if (awarded !== 0 && Math.floor(awarded) !== Number(awarded)) {
    // Determine the number of digits after the decimal place, if any.
    const bits = ('' + awarded).split('.');
    if (bits.length === 2) {
      const digits = (bits[1] as string).length;

      // Set scale
      grade.numericScale = -1 * digits;
      // Multiply awarded value by the inverse of the stored scale to get the value to store
      // awarded is grade.numericAwarded here: it is non-zero, so not nullish.
      grade.numericAwarded = Math.round(awarded * Math.pow(10, digits));
    }
  }

  return grade;
}

// getLabelForSelectAwarded is an internal function fo use in getGradeForDisplay.
function getLabelForSelectAwarded(
  module: GradeModule,
  awarded: string | null | undefined,
): string {
  const options = module.get('gradeSchedule.select.values') ?? [];
  const selected = findBy(options, 'value', awarded);
  // findBy gives an option fragment or undefined.
  if (selected === undefined) {
    return '';
  }
  return selected.get('label');
}

// getGradeForDisplay formats a grade for display, based on the grading method and schedule.
export function getGradeForDisplay(
  module: GradeModule,
  grade: AssessmentGrade,
  plainText = false,
): number | string | { option: GradeOption; index: number } | null | undefined {
  let option: GradeOption | undefined;
  let options: GradeOptions;

  switch (module.get('gradeSchedule.awardMethod')) {
    case GradingMethod.NUMERIC:
      return grade.get('numericAwarded');

    case GradingMethod.SELECT:
      return getLabelForSelectAwarded(module, grade.get('selectAwarded'));

    case GradingMethod.MULTIPLE_SELECT:
      return (grade.get('multiSelectAwarded') ?? [])
        .map((val) => getLabelForSelectAwarded(module, val))
        .join(', ');

    case GradingMethod.ICON_SELECT:
      options = module.get('gradeSchedule.select.values') ?? [];
      option = findBy(options, 'value', grade.get('iconAwarded'));
      // findBy gives an option fragment or undefined.
      if (plainText) {
        if (option === undefined) {
          return '';
        }
        return option.get('description');
      }
      if (option !== undefined) {
        return { option, index: options.indexOf(option) };
      }
  }
}
